import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { LayoutDashboard, CalendarDays, Star, Newspaper, CalendarOff, Users, ShieldAlert, Stethoscope } from "lucide-react";
import { useApi, ADMIN_TOKEN_KEY } from "@/lib/api";
import { AdminLogin } from "./AdminLogin";
import { Logo } from "@/components/site/Logo";
import { AdminDashboard } from "./AdminDashboard";
import { AdminBookings } from "./AdminBookings";
import { AdminReviews } from "./AdminReviews";
import { AdminBlog } from "./AdminBlog";
import { AdminSchedule } from "./AdminSchedule";
import { AdminUsers } from "./AdminUsers";
import { AdminDoctor } from "./AdminDoctor";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, C: AdminDashboard },
  { id: "bookings", label: "Bookings", icon: CalendarDays, C: AdminBookings },
  { id: "reviews", label: "Reviews", icon: Star, C: AdminReviews },
  { id: "doctor", label: "Doctor profile", icon: Stethoscope, C: AdminDoctor },
  { id: "blog", label: "Blog", icon: Newspaper, C: AdminBlog },
  { id: "schedule", label: "Holidays", icon: CalendarOff, C: AdminSchedule },
  { id: "users", label: "Team", icon: Users, C: AdminUsers },
];

const Shell = ({ me, onLogout }) => {
  const [tab, setTab] = useState("dashboard");
  const T = TABS.find((t) => t.id === tab);
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 lg:flex-row" data-testid="admin-shell">
      <aside className="border-b border-slate-200 bg-white p-4 lg:sticky lg:top-0 lg:h-screen lg:w-64 lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between lg:block"><Logo small /><p className="mt-1 hidden text-xs font-bold uppercase tracking-widest text-slate-400 lg:block">Clinic admin</p></div>
        <nav className="mt-4 flex gap-1 overflow-x-auto lg:mt-8 lg:flex-col">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex shrink-0 items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${tab === t.id ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <t.icon className="h-4 w-4" />{t.label}
            </button>
          ))}
        </nav>
      </aside>
      <div className="flex-1">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <h1 className="text-2xl font-bold">{T.label}</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm font-semibold text-slate-500 hover:text-teal-700" data-testid="admin-view-site-link">View site</Link>
            <span className="hidden text-sm text-slate-500 sm:inline">{me.email || me.name}</span>
            {me.kind === "password"
              ? <button onClick={onLogout} data-testid="admin-logout-button" className="rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200">Log out</button>
              : <UserButton />}
          </div>
        </header>
        <main className="p-4 sm:p-6"><T.C /></main>
      </div>
    </div>
  );
};

const Gate = () => {
  const api = useApi();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api("get", "/me").then(setMe).catch(() => setErr(true)); }, [api]);
  if (err) return <p className="p-10 text-center">Could not load your account. Please refresh.</p>;
  if (!me) return <p className="p-10 text-center text-slate-500">Loading admin...</p>;
  if (me.role !== "admin") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center" data-testid="admin-access-denied">
        <ShieldAlert className="h-12 w-12 text-[#ff6b6b]" />
        <h1 className="text-3xl font-bold">Admins only</h1>
        <p className="max-w-sm text-slate-600">Your account ({me.email}) doesn't have clinic admin access. Ask the clinic owner to grant it from the Team page.</p>
        <div className="flex gap-3"><Link to="/" className="rounded-full bg-teal-600 px-6 py-3 text-sm font-bold text-white">Back to site</Link><UserButton /></div>
      </div>
    );
  }
  return <Shell me={me} />;
};

const PasswordGate = ({ onLogout }) => {
  const api = useApi();
  const [me, setMe] = useState(null);
  useEffect(() => { api("get", "/admin/whoami").then(setMe).catch(onLogout); }, [api, onLogout]);
  if (!me) return <p className="p-10 text-center text-slate-500">Loading admin...</p>;
  return <Shell me={me} onLogout={onLogout} />;
};

export default function Admin() {
  const [hasToken, setHasToken] = useState(() => !!localStorage.getItem(ADMIN_TOKEN_KEY));
  const logout = useCallback(() => { localStorage.removeItem(ADMIN_TOKEN_KEY); setHasToken(false); }, []);
  if (hasToken) return <PasswordGate onLogout={logout} />;
  return (
    <>
      <Show when="signed-in"><Gate /></Show>
      <Show when="signed-out"><AdminLogin onDone={() => setHasToken(true)} /></Show>
    </>
  );
}
